/**
 * MirrorMoment - The Gut-Punch
 * 
 * "You're making $8.50/hour"
 * 
 * This is the moment of truth. The effective hourly rate
 * compared to survival target and minimum wage.
 * 
 * If they're underpricing badly, this number turns red and shakes.
 */

import { motion } from 'framer-motion';

interface MirrorMomentProps {
  effectiveHourly: number; // in cents
  survivalTarget: number;  // in cents
  isVisible: boolean;
  delay?: number;
}

const MINIMUM_WAGE = 1500; // $15/hr in cents

function formatRate(cents: number): string {
  const dollars = cents / 100;
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(dollars);
}

export function MirrorMoment({ 
  effectiveHourly, 
  survivalTarget,
  isVisible, 
  delay = 0 
}: MirrorMomentProps) {
  // Determine how bad it is
  const isBelowMinWage = effectiveHourly < MINIMUM_WAGE;
  const isBelowSurvival = effectiveHourly < survivalTarget;
  const isNegative = effectiveHourly < 0;
  const isHealthy = effectiveHourly >= survivalTarget;
  
  // Severity level
  const severity = isNegative ? 'critical' : isBelowMinWage ? 'severe' : isBelowSurvival ? 'warning' : 'healthy';
  
  const config = {
    critical: {
      rateColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      label: "You're paying to work",
      sublabel: 'Negative effective rate',
      icon: '💸',
      shake: true,
    },
    severe: {
      rateColor: 'text-red-600',
      bgColor: 'bg-red-50',
      borderColor: 'border-red-200',
      label: 'Below minimum wage',
      sublabel: `${formatRate(MINIMUM_WAGE)}/hr is the legal floor`,
      icon: '⚠️',
      shake: true,
    },
    warning: {
      rateColor: 'text-amber-600',
      bgColor: 'bg-amber-50',
      borderColor: 'border-amber-200',
      label: 'Below survival target',
      sublabel: `You need ${formatRate(survivalTarget)}/hr to cover costs`,
      icon: '📉',
      shake: false,
    },
    healthy: {
      rateColor: 'text-emerald-600',
      bgColor: 'bg-emerald-50',
      borderColor: 'border-emerald-200',
      label: 'Sustainable rate',
      sublabel: `Above your ${formatRate(survivalTarget)}/hr target`,
      icon: '✓',
      shake: false,
    },
  };
  
  const { rateColor, bgColor, borderColor, label, sublabel, icon, shake } = config[severity];

  // Shake animation for bad numbers
  const shakeAnimation = shake ? {
    x: [0, -4, 4, -4, 4, -2, 2, 0],
  } : {};

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={isVisible ? { 
        opacity: 1, 
        y: 0,
        ...shakeAnimation,
      } : { opacity: 0, y: 20 }}
      transition={{
        delay: delay / 1000,
        duration: 0.5,
        ease: [0.34, 1.56, 0.64, 1],
        x: shake ? {
          delay: (delay + 300) / 1000,
          duration: 0.5,
          ease: 'easeInOut',
        } : undefined,
      }}
      className={`
        relative overflow-hidden
        rounded-xl border
        ${bgColor} ${borderColor}
      `}
    >
      {/* Header */}
      <div className="px-4 pt-4 pb-2">
        <p className="text-xs font-medium uppercase tracking-wide text-charcoal-500">
          Your Effective Hourly Rate
        </p>
      </div>
      
      {/* The Number - This is the mirror */}
      <div className="px-4 pb-2">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={isVisible ? { scale: 1, opacity: 1 } : { scale: 0.8, opacity: 0 }}
          transition={{
            delay: (delay + 150) / 1000,
            duration: 0.4,
            ease: [0.34, 1.56, 0.64, 1],
          }}
          className="flex items-baseline gap-1"
        >
          <span className={`text-3xl font-black ${rateColor} tabular-nums`}>
            {isNegative ? '-' : ''}{formatRate(Math.abs(effectiveHourly))}
          </span>
          <span className={`text-lg font-semibold ${rateColor}`}>/hr</span>
        </motion.div>
      </div>
      
      {/* Context label */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={isVisible ? { opacity: 1 } : { opacity: 0 }}
        transition={{
          delay: (delay + 400) / 1000,
          duration: 0.3,
        }}
        className="px-4 pb-4"
      >
        <div className="flex items-center gap-2">
          <span className="text-base">{icon}</span>
          <div>
            <p className={`text-sm font-semibold ${rateColor}`}>
              {label}
            </p>
            <p className="text-xs text-charcoal-500">
              {sublabel}
            </p>
          </div>
        </div>
      </motion.div>
      
      {/* Dramatic red pulse for critical/severe */}
      {(severity === 'critical' || severity === 'severe') && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={isVisible ? { 
            opacity: [0, 0.15, 0],
          } : { opacity: 0 }}
          transition={{
            delay: (delay + 200) / 1000,
            duration: 1.5,
            ease: 'easeOut',
          }}
          className="absolute inset-0 bg-red-500 pointer-events-none"
        />
      )}
    </motion.div>
  );
}

export default MirrorMoment;
